import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import * as THREE from "three";
import "./EditProduct.css";
import { Canvas } from "@react-three/fiber";
import ThreeDModelViewer from "../../Model/ThreeDModelViewer";
import { OrbitControls } from "@react-three/drei";

const EditProduct = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const canvasRef = useRef();
  const [showDropdown, setShowDropdown] = useState(false);

  const [formData, setFormData] = useState({
    productName: "",
    productPrice: "",
    description: "",
    colors: [],
    size:[],
    image: "",
  });

  useEffect(() => {
    if (id) {
      axios
        .get(`${process.env.REACT_APP_BACKEND_SERVER}/api/products/${id}`)
        .then((res) => {
          console.log("res", res);

          setFormData({
            productName: res.data.title,
            productPrice: res.data.price,
            description: res.data.description,
            colors: Array.isArray(formData.colors)
              ? formData.colors.join(",")
              : formData.colors,
            size: Array.isArray(res.data.size)
              ? res.data.size
              : typeof res.data.size === "string"
              ? res.data.size.split(",").map((s) => s.trim())
              : [],
            image: res.data.path,
          });
        })
        .catch((err) => {
          console.error("Product fetch error", err);
        });
    }
  }, [id]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.put(
        `${process.env.REACT_APP_BACKEND_SERVER}/api/products/${id}`,
        formData,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      alert("Product updated!");
      navigate("/admin/products");
    } catch (err) {
      console.error(err);
      alert("Update failed");
    }
  };

  const colorHexToName = {
    "#FF5733": "Red Orange",
    "#F8C471": "Soft Orange",
    "#F0F3F4": "Light Gray",
    "#33C1FF": "Sky Blue",
    "#33FF57": "Lime Green",
    "#8E44AD": "Purple",
    "#FFC300": "Yellow",
    "#FF33A8": "Pink Magenta",
  };
  const colors = ["#FF33A8","#FFC300","#8E44AD","#FF5733", "#F8C471", "#33C1FF", "#F0F3F4", "#33FF57"];

  
  

  return (
    <div className="editProductPage">
      <h2 style={{ color: "#da6725" }}>Edit Product</h2>

      <div className="editProductContent">
        {/* Left - Product Form */}
        <form className="editProductForm" onSubmit={handleSubmit}>
          <div className="formGroup">
            <label>Product Name:</label>
            <input
              type="text"
              name="productName"
              value={formData.productName}
              onChange={handleChange}
              placeholder="Enter product name"
            />
          </div>
          <div className="formGroup">
            <label>Product Price:</label>
            <input
              type="number"
              name="productPrice"
              value={formData.productPrice}
              onChange={handleChange}
              placeholder="Enter price"
            />
          </div>
          <div className="formGroup">
            <label>Description:</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows="4"
              placeholder="Enter product description"
            ></textarea>
          </div>
          <div className="productSize">
            <h4>Sizes:</h4>
            <div className="sizeBtn">
              {Array.isArray(formData.size)
                ? formData.size.map((s, index) => (
                    <button type="button" key={index}>
                      {s}
                    </button>
                  ))
                : null}
            </div>
          </div>
          <div className="formGroup">
            <label>Available Colors:</label>

            <button
              type="button"
              onClick={() => setShowDropdown(!showDropdown)}
              style={{
                padding: "5px 10px",
                backgroundColor: "#eee",
                border: "1px solid #ccc",
                cursor: "pointer",
              }}
            >
              {showDropdown ? "Hide Color Options" : "Select Colors"}
            </button>

            {showDropdown && (
              <div
                style={{
                  border: "1px solid #ccc",
                  marginTop: "8px",
                  padding: "5px",
                }}
              >
                {Object.entries(colorHexToName).map(([hex, name]) => (
                  <div
                    key={hex}
                    onClick={() => {
                      if (!formData.colors.includes(hex)) {
                        setFormData({
                          ...formData,
                          colors: [...formData.colors, hex],
                        });
                      }
                    }}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      cursor: "pointer",
                      marginBottom: "4px",
                    }}
                  >
                    <div
                      style={{
                        backgroundColor: hex,
                        width: 20,
                        height: 20,
                        borderRadius: "50%",
                        marginRight: 8,
                        border: "1px solid #999",
                      }}
                    ></div>
                    {name}
                  </div>
                ))}
              </div>
            )}

            <div style={{ marginTop: "8px" }}>
              <strong>Selected Colors:</strong>{" "}
              {Array.isArray(formData.colors)
                ? formData.colors.join(", ")
                : formData.colors}
            </div>
          </div>

          <button type="submit" className="updateBtn">
            Update Product
          </button>
        </form>

        <div className="productPreview">
          <h3 style={{ color: "#da6725" }}>3D Model Preview</h3>
          <Canvas
            style={{ marginTop: "20px" }}
            camera={{ position: [0, 0, 3], fov: 35 }}
          >
            <ambientLight intensity={0.6} />
            <directionalLight position={[2, 3, 3]} />
            <React.Suspense fallback={null}>
              <ThreeDModelViewer path={formData.image} />
            </React.Suspense>
            <OrbitControls
              enableZoom={false}
              enablePan={false}
              autoRotate
              autoRotateSpeed={1.5}
              maxPolarAngle={Math.PI / 2}
              minPolarAngle={Math.PI / 2}
            />
          </Canvas>
          <div
            style={{
              marginTop: "20px",
              display: "flex",
              flexWrap: "wrap",
              gap: "10px",
              marginLeft: "100px",
            }}
          >
            {colors.map((color, index) => {
              const colorName = colorHexToName[color] || color; // Fallback to HEX if not mapped
              return (
                <button
                  key={index}
                  style={{
                    backgroundColor: color,
                    width: 20,
                    height: 20,
                    borderRadius: "50%",
                    border: "1px solid black",
                    outline: "none",
                    cursor: "pointer",
                  }}
                  title={colorName}
                />
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditProduct;
